import React, { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical, Image, Save, Loader2, Eye, EyeOff, ChevronUp, ChevronDown, Package } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { toast } from '../../hooks/use-toast';
import { homepageAPI, productsAPI } from '../../services/api';
import AdminLayout from '../../components/admin/AdminLayout';

const GRADIENT_OPTIONS = [
  { value: 'from-orange-600 via-orange-500 to-amber-500', label: 'Orange/Amber', preview: 'bg-gradient-to-r from-orange-600 via-orange-500 to-amber-500' },
  { value: 'from-teal-600 via-emerald-600 to-green-600', label: 'Teal/Green', preview: 'bg-gradient-to-r from-teal-600 via-emerald-600 to-green-600' },
  { value: 'from-purple-600 via-violet-600 to-indigo-600', label: 'Purple/Indigo', preview: 'bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-600' },
  { value: 'from-pink-600 via-rose-600 to-red-600', label: 'Pink/Red', preview: 'bg-gradient-to-r from-pink-600 via-rose-600 to-red-600' },
  { value: 'from-blue-600 via-cyan-600 to-teal-600', label: 'Blue/Cyan', preview: 'bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600' },
  { value: 'from-amber-600 via-yellow-500 to-orange-500', label: 'Gold/Yellow', preview: 'bg-gradient-to-r from-amber-600 via-yellow-500 to-orange-500' },
];

const CATEGORY_GRADIENT_OPTIONS = [
  { value: '', label: 'Auto (by position)', preview: '' },
  { value: 'from-orange-500 to-amber-500', label: 'Orange', preview: 'bg-gradient-to-br from-orange-500 to-amber-500' },
  { value: 'from-teal-500 to-emerald-500', label: 'Teal', preview: 'bg-gradient-to-br from-teal-500 to-emerald-500' },
  { value: 'from-purple-500 to-indigo-500', label: 'Purple', preview: 'bg-gradient-to-br from-purple-500 to-indigo-500' },
  { value: 'from-pink-500 to-rose-500', label: 'Pink', preview: 'bg-gradient-to-br from-pink-500 to-rose-500' },
  { value: 'from-blue-500 to-cyan-500', label: 'Blue', preview: 'bg-gradient-to-br from-blue-500 to-cyan-500' },
  { value: 'from-green-600 to-teal-600', label: 'Green', preview: 'bg-gradient-to-br from-green-600 to-teal-600' },
  { value: 'from-violet-500 to-purple-500', label: 'Violet', preview: 'bg-gradient-to-br from-violet-500 to-purple-500' },
  { value: 'from-red-500 to-orange-500', label: 'Red', preview: 'bg-gradient-to-br from-red-500 to-orange-500' },
  { value: 'from-slate-600 to-slate-800', label: 'Slate', preview: 'bg-gradient-to-br from-slate-600 to-slate-800' },
  { value: 'from-amber-500 to-orange-600', label: 'Amber', preview: 'bg-gradient-to-br from-amber-500 to-orange-600' },
];

const BADGE_OPTIONS = [
  'NEW ARRIVALS',
  'BEST SELLERS',
  'SALE',
  'LIMITED EDITION',
  'TRENDING',
  'HOT DEAL',
];

const AdminHomepage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sliders, setSliders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sectionTitle, setSectionTitle] = useState('Shop by Category');
  const [sectionSubtitle, setSectionSubtitle] = useState('Find exactly what brings you peace');
  const [activeTab, setActiveTab] = useState('sliders');
  
  // Best sellers state
  const [bestSellersTitle, setBestSellersTitle] = useState('Best Sellers');
  const [bestSellersSubtitle, setBestSellersSubtitle] = useState('Our most loved products');
  const [bestSellersBadge, setBestSellersBadge] = useState('TOP PICKS');
  const [allProducts, setAllProducts] = useState([]);
  const [featuredProductIds, setFeaturedProductIds] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [slidersRes, categoriesRes, settingsRes, productsRes] = await Promise.all([
        homepageAPI.getSliders(),
        homepageAPI.getCategories(),
        homepageAPI.getSettings(),
        productsAPI.getAll({ limit: 100 })
      ]);
      
      setSliders(slidersRes.data || []);
      console.log('Loaded sliders:', slidersRes.data);
      setCategories(categoriesRes.data?.categories || []);
      setSectionTitle(categoriesRes.data?.section_title || 'Shop by Category');
      setSectionSubtitle(categoriesRes.data?.section_subtitle || 'Find exactly what brings you peace');
      
      // Best sellers settings
      setBestSellersTitle(settingsRes.data?.best_sellers_title || 'Best Sellers');
      setBestSellersSubtitle(settingsRes.data?.best_sellers_subtitle || 'Our most loved products');
      setBestSellersBadge(settingsRes.data?.best_sellers_badge || 'TOP PICKS');
      setFeaturedProductIds(settingsRes.data?.featured_product_ids || []);
      
      setAllProducts(productsRes.data || []);
    } catch (error) {
      console.error('Error fetching homepage data:', error);
      toast({ title: 'Error', description: 'Failed to load homepage settings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSliderChange = (index, field, value) => {
    const updated = [...sliders];
    updated[index] = { ...updated[index], [field]: value };
    setSliders(updated);
  };

  const handleAddSlider = () => {
    setSliders([...sliders, {
      title: 'New Slide',
      subtitle: 'Add your subtitle here',
      cta_text: 'Shop Now',
      link: '/shop',
      image: '',
      badge_text: 'NEW ARRIVALS',
      gradient: 'from-orange-600 via-orange-500 to-amber-500',
      is_active: true
    }]);
  };

  const handleDeleteSlider = (index) => {
    if (sliders.length <= 1) {
      toast({ title: 'Error', description: 'You need at least one slider', variant: 'destructive' });
      return;
    }
    if (window.confirm('Delete this slider? Click "Save Sliders" after to apply changes.')) {
      const updated = sliders.filter((_, i) => i !== index);
      setSliders(updated);
      toast({ title: 'Slider removed', description: 'Click "Save Sliders" to apply changes' });
    }
  };

  const handleMoveSlider = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sliders.length) return;
    
    const updated = [...sliders];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setSliders(updated);
  };

  const handleCategoryChange = (index, field, value) => {
    const updated = [...categories];
    updated[index] = { ...updated[index], [field]: value };
    setCategories(updated);
  };

  const handleMoveCategoryOrder = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= categories.length) return;
    
    const updated = [...categories];
    // Swap order values
    const tempOrder = updated[index].order;
    updated[index].order = updated[newIndex].order;
    updated[newIndex].order = tempOrder;
    // Swap positions in array
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setCategories(updated);
  };

  const handleSaveSliders = async () => {
    setSaving(true);
    try {
      await homepageAPI.updateSettings({ sliders });
      toast({ title: 'Success', description: 'Sliders saved successfully!' });
    } catch (error) {
      console.error('Error saving sliders:', error);
      toast({ title: 'Error', description: 'Failed to save sliders', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCategories = async () => {
    setSaving(true);
    try {
      const featured_categories = categories.map((cat, idx) => ({
        category_id: cat.id,
        display_name: cat.display_name,
        custom_image: cat.image !== cat.original_image ? cat.image : null,
        gradient: cat.gradient || null,
        order: idx,
        is_visible: cat.is_visible
      }));
      
      await homepageAPI.updateSettings({
        section_title: sectionTitle,
        section_subtitle: sectionSubtitle,
        featured_categories
      });
      toast({ title: 'Success', description: 'Category section saved successfully!' });
    } catch (error) {
      console.error('Error saving categories:', error);
      toast({ title: 'Error', description: 'Failed to save categories', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBestSellers = async () => {
    setSaving(true);
    try {
      await homepageAPI.updateSettings({
        best_sellers_title: bestSellersTitle,
        best_sellers_subtitle: bestSellersSubtitle,
        best_sellers_badge: bestSellersBadge,
        featured_product_ids: featuredProductIds
      });
      toast({ title: 'Success', description: 'Best sellers section saved successfully!' });
    } catch (error) {
      console.error('Error saving best sellers:', error);
      toast({ title: 'Error', description: 'Failed to save best sellers section', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Add product to featured list
  const handleAddFeatured = (productId) => {
    if (featuredProductIds.includes(productId)) return;
    if (featuredProductIds.length >= 8) {
      toast({ title: 'Limit reached', description: 'Maximum 8 featured products allowed', variant: 'destructive' });
      return;
    }
    setFeaturedProductIds([...featuredProductIds, productId]);
  };

  // Remove product from featured list
  const handleRemoveFeatured = (productId) => {
    setFeaturedProductIds(featuredProductIds.filter(id => id !== productId));
  };

  // Move product up in featured list
  const handleMoveFeatured = (index, direction) => {
    const newList = [...featuredProductIds];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newList.length) return;
    [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
    setFeaturedProductIds(newList);
  };

  const handleToggleBestSeller = async (productId, currentValue) => {
    try {
      const product = allProducts.find(p => (p._id || p.id) === productId);
      if (!product) return;
      
      await productsAPI.update(productId, { ...product, is_best_seller: !currentValue });
      
      // Update local state
      setAllProducts(prev => prev.map(p => 
        (p._id || p.id) === productId ? { ...p, is_best_seller: !currentValue } : p
      ));
      
      toast({ title: 'Success', description: `Product ${!currentValue ? 'added to' : 'removed from'} Best Sellers` });
    } catch (error) {
      console.error('Error updating product:', error);
      toast({ title: 'Error', description: 'Failed to update product', variant: 'destructive' });
    }
  };

  const handleImageUpload = async (e, type, index) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/image`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();
      
      if (type === 'slider') {
        handleSliderChange(index, 'image', data.url);
      } else if (type === 'category') {
        handleCategoryChange(index, 'image', data.url);
      }
      
      toast({ title: 'Success', description: 'Image uploaded!' });
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: 'Error', description: 'Failed to upload image', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Homepage Settings</h1>
            <p className="text-gray-500 mt-1">Customize your homepage sliders and category section</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('sliders')}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'sliders'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Hero Sliders
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'categories'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Category Section
          </button>
          <button
            onClick={() => setActiveTab('bestsellers')}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'bestsellers'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Best Sellers
          </button>
        </div>

        {/* Sliders Tab */}
        {activeTab === 'sliders' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500">Manage hero slideshow (3-4 slides recommended)</p>
              <div className="flex gap-2">
                <Button onClick={handleAddSlider} variant="outline" className="gap-2">
                  <Plus className="h-4 w-4" /> Add Slide
                </Button>
                <Button onClick={handleSaveSliders} disabled={saving} className="gap-2 bg-orange-500 hover:bg-orange-600">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Sliders
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              {sliders.map((slider, index) => (
                <div
                  key={index}
                  className={`border rounded-lg p-6 bg-white shadow-sm ${!slider.is_active ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Move controls */}
                    <div className="flex flex-col gap-1 pt-2">
                      <button
                        onClick={() => handleMoveSlider(index, 'up')}
                        disabled={index === 0}
                        className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <GripVertical className="h-4 w-4 text-gray-400 mx-auto" />
                      <button
                        onClick={() => handleMoveSlider(index, 'down')}
                        disabled={index === sliders.length - 1}
                        className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Image preview */}
                    <div className="flex-shrink-0">
                      <div className="w-48 h-28 rounded-lg overflow-hidden bg-gray-100 relative group">
                        {slider.image ? (
                          <>
                            <img src={slider.image} alt="Slide" className="w-full h-full object-cover" />
                            <div className={`absolute inset-0 bg-gradient-to-r ${slider.gradient} opacity-70`} />
                          </>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <Image className="h-8 w-8" />
                          </div>
                        )}
                        <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                          <span className="text-white text-sm">Change Image</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleImageUpload(e, 'slider', index)}
                          />
                        </label>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Title</Label>
                        <Input
                          value={slider.title}
                          onChange={(e) => handleSliderChange(index, 'title', e.target.value)}
                          placeholder="Slide title"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Subtitle</Label>
                        <Input
                          value={slider.subtitle}
                          onChange={(e) => handleSliderChange(index, 'subtitle', e.target.value)}
                          placeholder="Slide subtitle"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Button Text</Label>
                        <Input
                          value={slider.cta_text}
                          onChange={(e) => handleSliderChange(index, 'cta_text', e.target.value)}
                          placeholder="Shop Now"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Link</Label>
                        <Input
                          value={slider.link}
                          onChange={(e) => handleSliderChange(index, 'link', e.target.value)}
                          placeholder="/shop"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Badge</Label>
                        <select
                          value={slider.badge_text}
                          onChange={(e) => handleSliderChange(index, 'badge_text', e.target.value)}
                          className="w-full h-10 px-3 border rounded-md text-sm"
                        >
                          {BADGE_OPTIONS.map(badge => (
                            <option key={badge} value={badge}>{badge}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Gradient Color</Label>
                        <select
                          value={slider.gradient}
                          onChange={(e) => handleSliderChange(index, 'gradient', e.target.value)}
                          className="w-full h-10 px-3 border rounded-md text-sm"
                        >
                          {GRADIENT_OPTIONS.map(g => (
                            <option key={g.value} value={g.value}>{g.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Image URL</Label>
                        <Input
                          value={slider.image}
                          onChange={(e) => handleSliderChange(index, 'image', e.target.value)}
                          placeholder="https://..."
                        />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={slider.is_active}
                          onCheckedChange={(checked) => handleSliderChange(index, 'is_active', checked)}
                        />
                        <span className="text-xs text-gray-500">{slider.is_active ? 'Active' : 'Hidden'}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSlider(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500">Customize the "Shop by Category" section</p>
              <Button onClick={handleSaveCategories} disabled={saving} className="gap-2 bg-orange-500 hover:bg-orange-600">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save Categories
              </Button>
            </div>

            {/* Section header settings */}
            <div className="bg-white border rounded-lg p-6 space-y-4">
              <h3 className="font-semibold text-gray-900">Section Header</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Section Title</Label>
                  <Input
                    value={sectionTitle}
                    onChange={(e) => setSectionTitle(e.target.value)}
                    placeholder="Shop by Category"
                  />
                </div>
                <div>
                  <Label>Section Subtitle</Label>
                  <Input
                    value={sectionSubtitle}
                    onChange={(e) => setSectionSubtitle(e.target.value)}
                    placeholder="Find exactly what brings you peace"
                  />
                </div>
              </div>
            </div>

            {/* Category list */}
            <div className="bg-white border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Order</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Image</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Original Name</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Display Name</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Color</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Products</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Visible</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {categories.map((category, index) => (
                    <tr key={category.id} className={!category.is_visible ? 'bg-gray-50 opacity-60' : ''}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleMoveCategoryOrder(index, 'up')}
                            disabled={index === 0}
                            className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          <span className="text-sm text-gray-500 w-6 text-center">{index + 1}</span>
                          <button
                            onClick={() => handleMoveCategoryOrder(index, 'down')}
                            disabled={index === categories.length - 1}
                            className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="relative w-16 h-16 rounded overflow-hidden bg-gray-100 group">
                          {category.image ? (
                            <img src={category.image} alt={category.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <Image className="h-6 w-6" />
                            </div>
                          )}
                          <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                            <Image className="h-4 w-4 text-white" />
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleImageUpload(e, 'category', index)}
                            />
                          </label>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-500">{category.name}</span>
                      </td>
                      <td className="px-4 py-3">
                        <Input
                          value={category.display_name || ''}
                          onChange={(e) => handleCategoryChange(index, 'display_name', e.target.value)}
                          placeholder={category.name}
                          className="max-w-[140px]"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <select
                            value={category.gradient || ''}
                            onChange={(e) => handleCategoryChange(index, 'gradient', e.target.value)}
                            className="w-28 h-9 px-2 border rounded text-xs"
                          >
                            {CATEGORY_GRADIENT_OPTIONS.map(g => (
                              <option key={g.value} value={g.value}>{g.label}</option>
                            ))}
                          </select>
                          {category.gradient && (
                            <div className={`w-8 h-8 rounded bg-gradient-to-br ${category.gradient}`} />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">{category.product_count}</span>
                      </td>
                      <td className="px-4 py-3">
                        <Switch
                          checked={category.is_visible !== false}
                          onCheckedChange={(checked) => handleCategoryChange(index, 'is_visible', checked)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Best Sellers Tab */}
        {activeTab === 'bestsellers' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500">Customize the "Best Sellers" section and manage featured products</p>
              <Button onClick={handleSaveBestSellers} disabled={saving} className="gap-2 bg-orange-500 hover:bg-orange-600">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save Settings
              </Button>
            </div>

            {/* Section header settings */}
            <div className="bg-white border rounded-lg p-6 space-y-4">
              <h3 className="font-semibold text-gray-900">Section Header</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Section Title</Label>
                  <Input
                    value={bestSellersTitle}
                    onChange={(e) => setBestSellersTitle(e.target.value)}
                    placeholder="Best Sellers"
                  />
                </div>
                <div>
                  <Label>Section Subtitle</Label>
                  <Input
                    value={bestSellersSubtitle}
                    onChange={(e) => setBestSellersSubtitle(e.target.value)}
                    placeholder="Our most loved products"
                  />
                </div>
                <div>
                  <Label>Badge Text</Label>
                  <Input
                    value={bestSellersBadge}
                    onChange={(e) => setBestSellersBadge(e.target.value)}
                    placeholder="TOP PICKS"
                  />
                </div>
              </div>
            </div>

            {/* Featured Products Selection */}
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="p-4 border-b bg-orange-50">
                <h3 className="font-semibold text-gray-900">Featured Products (Top Picks)</h3>
                <p className="text-sm text-gray-600">Select up to 8 products to display in the "Top Picks" section. Drag to reorder. These will override the automatic "Best Seller" selection.</p>
              </div>
              
              {/* Selected Featured Products */}
              {featuredProductIds.length > 0 ? (
                <div className="p-4 space-y-2">
                  {featuredProductIds.map((productId, index) => {
                    const product = allProducts.find(p => (p._id || p.id) === productId);
                    if (!product) return null;
                    return (
                      <div key={productId} className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <span className="text-sm font-bold text-orange-600 w-6">{index + 1}</span>
                        <div className="w-12 h-12 rounded overflow-hidden bg-gray-100 flex-shrink-0">
                          {product.images?.[0] ? (
                            <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <Package className="h-5 w-5" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{product.name}</p>
                          <p className="text-sm text-gray-500">${(product.sale_price || product.price || 0).toFixed(2)}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleMoveFeatured(index, 'up')}
                            disabled={index === 0}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleMoveFeatured(index, 'down')}
                            disabled={index === featuredProductIds.length - 1}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveFeatured(productId)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No featured products selected</p>
                  <p className="text-sm">Add products from the list below</p>
                </div>
              )}
            </div>

            {/* Product Search and Add */}
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="p-4 border-b bg-gray-50">
                <h3 className="font-semibold text-gray-900">Add Products to Featured</h3>
                <div className="mt-2 flex gap-3">
                  <Input
                    placeholder="Search products by name..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="max-w-md"
                  />
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">All Categories</option>
                    {[...new Set(allProducts.map(p => p.category).filter(Boolean))].sort().map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Image</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Product Name</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Price</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Category</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {allProducts
                      .filter(p => !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()))
                      .filter(p => !categoryFilter || p.category === categoryFilter)
                      .slice(0, 50)
                      .map((product) => {
                        const productId = product._id || product.id;
                        const isSelected = featuredProductIds.includes(productId);
                        return (
                          <tr key={productId} className={isSelected ? 'bg-orange-50' : 'hover:bg-gray-50'}>
                            <td className="px-4 py-3">
                              <div className="w-10 h-10 rounded overflow-hidden bg-gray-100">
                                {product.images?.[0] ? (
                                  <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                                    <Package className="h-4 w-4" />
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="font-medium text-gray-900 text-sm">{product.name.substring(0, 50)}{product.name.length > 50 ? '...' : ''}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-gray-600 text-sm">${(product.sale_price || product.price || 0).toFixed(2)}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-xs text-gray-500">{product.category}</span>
                            </td>
                            <td className="px-4 py-3">
                              {isSelected ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRemoveFeatured(productId)}
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                >
                                  Remove
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  onClick={() => handleAddFeatured(productId)}
                                  disabled={featuredProductIds.length >= 8}
                                  className="bg-orange-500 hover:bg-orange-600"
                                >
                                  <Plus className="h-4 w-4 mr-1" />
                                  Add
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminHomepage;
